import { call, put, select } from 'redux-saga/effects';

import {
  post,
  apiCallWithApiKey,
  X_API_KEY_ADMIN,
} from '../services/api';
import { isValidZipCode } from '../services/utils';
import {
  refreshPetsAfterSubmitDone,
  submitPetDemographicsFailure,
  submitPetDemographicsSuccess,
  updateCustomerEmailFailed,
  updateCustomerEmailSuccess,
  validateCertificateZipCodeAttempt,
  validateCertificateZipCodeFailed,
  validateCertificateZipCodeSuccess,
  validateEmailFailed,
  validateEmailSuccess,
  verifyCustomerFailed,
  verifyCustomerSuccess,
} from '../actions';
import {
  buildPetDemographicsBody,
} from '../components/petDemographics/petDemographics.utils';
import { getPolicySummary } from './policies';

export function* submitPetDemographicsSaga(dispatch, { payload }) {
  try {
    const {
      policyDisplayNumber,
      declarations,
      coveragePeriodPetsData,
    } = payload;

    const store = yield select(({ session }) => ({ session }));
    const pdbaDataItem = store.session?.userInfo?.PdbaData?.[0];
    const originalPetDemographics = pdbaDataItem?.NeedsPetDemographics;
    const needsPetDemographics = originalPetDemographics[policyDisplayNumber]
      ?.demographics;

    const body = buildPetDemographicsBody({
      coveragePeriodPetsData,
      declarations,
      needsPetDemographics,
      policyDisplayNumber,
    });

    const response = yield call(
      post,
      dispatch,
      'customer/Pet/pdba/demographics',
      body,
    );

    if (response && response.success) {
      yield put(submitPetDemographicsSuccess());
    } else {
      yield put(submitPetDemographicsFailure());
    }
  } catch (e) {
    yield put(submitPetDemographicsFailure());
  }
}

export function* refreshPetsAfterSubmitSaga(dispatch) {
  try {
    yield call(getPolicySummary, dispatch);
  } finally {
    yield put(refreshPetsAfterSubmitDone());
  }
}

export function* verifyCustomer(dispatch, { payload }) {
  try {
    const {
      email, firstName, groupNumber, lastName, yearMonthOfHire, zipCode,
    } = payload;

    const queryParams = {
      email,
      firstName,
      groupNumber,
      lastName,
      zipCode,
      ...(yearMonthOfHire && { yearMonthOfHire }),
    };

    const response = yield call(
      apiCallWithApiKey,
      'customer/PetPortalCertificate',
      {
        headers: X_API_KEY_ADMIN,
        params: queryParams,
      },
      dispatch,
    );

    if (response && response.success) {
      const { certificates = [] } = response;
      yield put(verifyCustomerSuccess({ certificates }));
    } else {
      yield put(verifyCustomerFailed({
        error: response.error || response.message,
      }));
    }
  } catch (exception) {
    yield put(verifyCustomerFailed({
      error: exception.error || exception.message,
    }));
  }
}

export function* validateEmail(dispatch, { payload }) {
  try {
    const { email } = payload;

    const response = yield call(
      apiCallWithApiKey,
      `customer/PetPortalCertificate/linked?email=${encodeURIComponent(email)}`,
      {
        headers: X_API_KEY_ADMIN,
      },
      dispatch,
    );

    if (response && response.success) {
      yield put(validateEmailSuccess({
        emailExists: response.emailExists,
        // WIP: b2c credentials property has not be added in backend yet
        hasB2CCredentials: response.hasB2CCredentials || true,
        hasGroupPolicy: response.hasGroupPolicy,
      }));
    } else {
      yield put(validateEmailFailed({
        error: response.error || response.message,
      }));
    }
  } catch (exception) {
    yield put(validateEmailFailed({
      error: exception.error || exception.message,
    }));
  }
}

export function* updateCustomerEmail(dispatch, { payload }) {
  try {
    const { email, certificateNumber } = payload;

    const response = yield call(
      apiCallWithApiKey,
      'customer/PetPortalCertificate/Email',
      {
        body: {
          certificateNumber: String(certificateNumber),
          email,
        },
        headers: X_API_KEY_ADMIN,
        method: 'PATCH',
      },
      dispatch,
      false,
    );

    if (response && response.success) {
      yield put(updateCustomerEmailSuccess());
    } else {
      yield put(updateCustomerEmailFailed({
        error: response.error || response.message || response.detail,
      }));
    }
  } catch (exception) {
    yield put(updateCustomerEmailFailed({
      error: exception.error || exception.message,
    }));
  }
}

export function* validateCertificateZipCode({ payload }) {
  const { zipCode } = payload;

  yield put(validateCertificateZipCodeAttempt());

  if (!isValidZipCode(zipCode)) {
    yield put(validateCertificateZipCodeFailed());
    return;
  }

  const response = yield call(
    apiCallWithApiKey,
    `/api/State/ValidateZipcode/${zipCode}`,
  );

  if (response.IsValid && response.Data) {
    yield put(validateCertificateZipCodeSuccess());
  } else {
    yield put(validateCertificateZipCodeFailed());
  }
}
